// src/api/axios.js
import axios from 'axios';
import { resolveLobbyApiBase } from '../config/serviceUrl';
import {
  clearStoredLoginSessionId,
  ensureFreshAccessToken,
  getAccessTokenSessionId,
  getStoredAccessToken,
  getStoredLoginSessionId,
  isCurrentLoginSession,
  isDefinitiveAuthFailure,
  refreshAccessToken,
} from './authSession';

const API_BASE = resolveLobbyApiBase();

const AUTH_SESSION_ERRORS = new Set([
  'SESSION_MISSING',
  'SESSION_EXPIRED',
  'SESSION_MISMATCH',
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_REVOKED',
  'NO_TOKEN',
]);

function resolveAuthFailureSessionId(error, original = {}) {
  return String(
    error?.response?.data?.request_session_id ||
    error?.response?.data?.session_id ||
    error?.requestSessionId ||
    original?.__npcRequestSessionId ||
    ''
  ).trim();
}

function clearAuthAndRedirectToLogin(reason = 'session', sessionId = '') {
  const failedSessionId = String(sessionId || '').trim();

  // Every async auth failure is session-scoped. A late response from session A
  // must never clear the token that now belongs to session B.
  if (!failedSessionId || !isCurrentLoginSession(failedSessionId)) {
    return false;
  }

  // socket.js가 로드된 로비/게임 화면에서는 동기 CustomEvent로 먼저 모든 로그인 종속 소켓을 끊는다.
  try {
    window.dispatchEvent(new CustomEvent('npc:auth-force-clear', {
      detail: { reason, session_id: failedSessionId },
    }));
  } catch (_) {}

  // The socket listener above normally performs the same clear synchronously.
  // Keep this local cleanup as a fallback for screens where socket.js is not mounted.
  if (!isCurrentLoginSession(failedSessionId) && !getStoredAccessToken()) return true;

  try {
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('jwt');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('user_code');
    localStorage.removeItem('userCode');
  } catch (_) {}
  clearStoredLoginSessionId();

  try { sessionStorage.removeItem('npc:guest-login:dismissed'); } catch (_) {}
  try {
    const target = `/?guestLogin=1&reason=${encodeURIComponent(reason)}`;
    const path = String(window.location?.pathname || '');
    const search = String(window.location?.search || '');
    if (path !== '/' || !search.includes('guestLogin=1')) {
      window.location.replace(target);
    }
  } catch (_) {}
  return true;
}

function attachAuthInterceptors(instance) {
  instance.interceptors.request.use(
    async (config) => {
      let token = getStoredAccessToken();
      if (token && !config.__npcSkipAuthRefresh) {
        try {
          token = await ensureFreshAccessToken();
        } catch (refreshError) {
          // Redis/네트워크 일시 장애는 기존 토큰으로 요청을 계속 시도한다.
          // 확정적인 세션 무효는 응답 인터셉터가 최종 처리한다.
          token = getStoredAccessToken();
        }
      }
      // Snapshot the exact login session that owns this request. This value is
      // intentionally immutable for the lifetime of the request/response pair.
      config.__npcRequestHadAuthToken = Boolean(token);
      config.__npcRequestSessionId = String(
        getAccessTokenSessionId(token) || getStoredLoginSessionId() || ''
      ).trim();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = Number(error?.response?.status || 0);
      const code = String(error?.response?.data?.error || '').trim();
      const url = String(error?.config?.url || '');
      const isLoginRequest = /(^|\/)login(\?|$)/.test(url);
      const isRefreshRequest = /\/session\/refresh(\?|$)/.test(url);
      const original = error?.config || {};
      const failedSessionId = resolveAuthFailureSessionId(error, original);

      // A response from an older login session is stale in its entirety. Do not
      // refresh/retry it under the new account/session, and never let it clear auth.
      if (failedSessionId && !isCurrentLoginSession(failedSessionId)) {
        return Promise.reject(error);
      }

      // Access Token만 만료된 경우 서버 로그인 세션이 살아있으면 새 토큰을 받아 1회 재시도한다.
      if (!isLoginRequest && !isRefreshRequest && original.__npcRequestHadAuthToken === true && status === 401 && code === 'TOKEN_EXPIRED' && !original.__npcAuthRetried) {
        try {
          const nextToken = await refreshAccessToken();
          original.__npcAuthRetried = true;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${nextToken}`;
          original.__npcRequestSessionId = getAccessTokenSessionId(nextToken) || getStoredLoginSessionId();
          return instance.request(original);
        } catch (refreshError) {
          if (isDefinitiveAuthFailure(refreshError)) {
            clearAuthAndRedirectToLogin(
              String(refreshError?.code || 'session'),
              String(refreshError?.requestSessionId || failedSessionId || original.__npcRequestSessionId || '').trim()
            );
          }
          // Do not propagate the original TOKEN_EXPIRED after a failed refresh.
          // On transient Redis/DB/network outages, a screen may misinterpret that
          // stale 401 as an instruction to destroy a still-valid login session.
          return Promise.reject(refreshError);
        }
      }

      // SESSION_STORE_UNAVAILABLE은 503 일시 장애이므로 절대 로컬 인증을 삭제하지 않는다.
      if (
        !isLoginRequest &&
        original.__npcRequestHadAuthToken === true &&
        status === 401 &&
        AUTH_SESSION_ERRORS.has(code)
      ) {
        clearAuthAndRedirectToLogin(code || 'session', failedSessionId);
      }
      return Promise.reject(error);
    }
  );
}

const api = axios.create({ baseURL: API_BASE });
attachAuthInterceptors(api);

const userApi = axios.create({ baseURL: `${API_BASE}/user` });
attachAuthInterceptors(userApi);

export default api;
export { userApi, clearAuthAndRedirectToLogin };
